import React, { useState, useEffect, useCallback } from 'react';
import { stats as statsApi } from '../utils/api';
import { tzOffset } from '../utils/date';
import { useToast } from '../components/Toast';

const QUEST_ICONS = { main: '👑', side: '⚔️', collectible: '💎', faction: '🛡️' };
const TYPE_COLORS = {
  main: 'var(--quest-main)', side: 'var(--quest-side)',
  collectible: 'var(--quest-collectible)', faction: 'var(--quest-faction)',
};

// Default goals — used as fallback when player hasn't set custom values
const GOAL_DEFAULTS = {
  calories: 2000, protein_g: 150, carbs_g: 300,
  fat_g: 65, sodium_mg: 2300, sugar_g: 50,
};

function StatBar({ label, value, max, color, unit = '' }) {
  const pct  = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const over = value > max && max > 0;
  return (
    <div className="stat-bar-row">
      <div className="stat-bar-label">
        <span style={{ color: over ? 'var(--accent-red)' : color }}>{label}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem' }}>
          <span style={{ color: over ? 'var(--accent-red)' : 'var(--text-primary)' }}>
            {Math.round(value)}{unit}
          </span>
          <span style={{ color: 'var(--text-muted)' }}>/{max}{unit}</span>
        </span>
      </div>
      <div className="stat-bar-track">
        <div className="stat-bar-fill" style={{
          width: `${pct}%`,
          background: over ? 'var(--accent-red)' : color,
          boxShadow: `0 0 6px ${over ? 'var(--accent-red)' : color}88`,
        }} />
      </div>
    </div>
  );
}

function localDateStr() {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

export default function PlayerDashboard({ activePlayer, activePlayerId }) {
  const toast = useToast();
  const [data,     setData]     = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    if (!activePlayerId) return;
    setLoading(true);
    try {
      const tz = tzOffset();
      const [d, up, act] = await Promise.all([
        statsApi.get(activePlayerId, tz),
        statsApi.upcoming(activePlayerId, 7),
        statsApi.activity(activePlayerId, 8),
      ]);
      setData(d);
      setUpcoming(up);
      setActivity(act);
    } catch (e) {
      toast('Failed to load dashboard data', 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [activePlayerId]);

  useEffect(() => { load(); }, [load]);

  if (!activePlayer) return <div className="empty-state"><p>Select a player to view HQ</p></div>;
  if (loading)       return <div className="empty-state"><p>Loading player data...</p></div>;
  if (!data)         return <div className="empty-state"><p>Failed to load data</p></div>;

  const { points, quests, food_today, budget } = data;
  const totalCompleted = Object.values(quests).reduce((s, q) => s + (q.completed || 0), 0);
  const totalActive    = Object.values(quests).reduce((s, q) => s + (q.active    || 0), 0);

  const level     = Math.floor(points.earned / 100) + 1;
  const xpInLevel = points.earned % 100;

  // Resolve player's custom nutrition goals with fallback to defaults
  const goals = {
    calories:  activePlayer.goal_calories  || GOAL_DEFAULTS.calories,
    protein_g: activePlayer.goal_protein_g || GOAL_DEFAULTS.protein_g,
    carbs_g:   activePlayer.goal_carbs_g   || GOAL_DEFAULTS.carbs_g,
    fat_g:     activePlayer.goal_fat_g     || GOAL_DEFAULTS.fat_g,
    sodium_mg: activePlayer.goal_sodium_mg || GOAL_DEFAULTS.sodium_mg,
    sugar_g:   activePlayer.goal_sugar_g   || GOAL_DEFAULTS.sugar_g,
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">⚔️ Player HQ</h2>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* ── Player card ─────────────────────────────────────────────────── */}
      <div className="player-hq-card">
        <div className="player-hq-avatar">
          {activePlayer.picture
            ? <img src={activePlayer.picture} alt="" />
            : <span>{activePlayer.name?.[0]}</span>
          }
          <div className="player-level-badge">LV{level}</div>
        </div>
        <div className="player-hq-info">
          <h3 className="player-hq-name">{activePlayer.name}</h3>
          {activePlayer.title && <div className="player-hq-title">{activePlayer.title}</div>}
          <div className="player-hq-stats">
            {activePlayer.age           && <span>Age {activePlayer.age}</span>}
            {activePlayer.height_inches && (
              <span>{Math.floor(activePlayer.height_inches / 12)}'{Math.round(activePlayer.height_inches % 12)}"</span>
            )}
            {activePlayer.weight_lbs   && <span>{activePlayer.weight_lbs} lbs</span>}
          </div>
          <div className="xp-bar-wrap">
            <div className="xp-bar-label">
              <span style={{ color: 'var(--accent-gold)' }}>LV{level} XP</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                {xpInLevel}/100 → LV{level + 1}
              </span>
            </div>
            <div className="xp-bar-track">
              <div className="xp-bar-fill" style={{ width: `${xpInLevel}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Points ──────────────────────────────────────────────────────── */}
      <div className="section-label">⭐ Points Balance</div>
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        <div className="kpi-card kpi-gold">
          <div className="kpi-value">{points.balance}</div>
          <div className="kpi-label">Balance</div>
        </div>
        <div className="kpi-card kpi-green">
          <div className="kpi-value">{points.earned}</div>
          <div className="kpi-label">Earned</div>
        </div>
        <div className="kpi-card kpi-magenta">
          <div className="kpi-value">{points.spent}</div>
          <div className="kpi-label">Spent</div>
        </div>
      </div>

      {/* ── Quests ──────────────────────────────────────────────────────── */}
      <div className="section-label">📜 Quest Progress</div>
      <div className="kpi-grid">
        <div className="kpi-card kpi-cyan">
          <div className="kpi-value">{totalActive}</div>
          <div className="kpi-label">Active</div>
        </div>
        <div className="kpi-card kpi-green">
          <div className="kpi-value">{totalCompleted}</div>
          <div className="kpi-label">Done</div>
        </div>
        {Object.entries(quests).map(([type, counts]) => (
          <div className="kpi-card" key={type} style={{ borderColor: TYPE_COLORS[type] }}>
            <div className="kpi-value" style={{ color: TYPE_COLORS[type], textShadow: `0 0 6px ${TYPE_COLORS[type]}` }}>
              {counts.completed || 0}
            </div>
            <div className="kpi-label">{QUEST_ICONS[type]} {type}</div>
          </div>
        ))}
      </div>

      {/* ── Upcoming quests ─────────────────────────────────────────────── */}
      {upcoming.length > 0 && (
        <>
          <div className="section-label">⏰ Coming Up (Next 7 Days)</div>
          <div className="upcoming-list">
            {upcoming.map(q => {
              const due    = q.due_date.split('T')[0];
              const today  = localDateStr();
              const diff   = Math.round(
                (new Date(due + 'T00:00:00') - new Date(today + 'T00:00:00')) / 86400000
              );
              return (
                <div className="upcoming-item" key={q.id}>
                  <span className={`badge badge-${q.type}`}>{QUEST_ICONS[q.type]}</span>
                  <span className="upcoming-name">{q.name}</span>
                  <span className="upcoming-due" style={{
                    color: diff === 0 ? 'var(--accent-gold)' : diff <= 2 ? 'var(--accent-orange)' : 'var(--text-muted)',
                  }}>
                    {diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : `In ${diff}d`}
                  </span>
                  <span className="upcoming-pts" style={{ color: TYPE_COLORS[q.type] }}>+{q.points}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Nutrition ───────────────────────────────────────────────────── */}
      <div className="section-label">🍖 Today's Nutrition</div>
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          {[
            { v: food_today.calories, l: 'Cal',    cls: 'kpi-orange',  sfx: ''   },
            { v: food_today.protein,  l: 'Protein', cls: 'kpi-cyan',    sfx: 'g'  },
            { v: food_today.carbs,    l: 'Carbs',   cls: 'kpi-gold',    sfx: 'g'  },
            { v: food_today.fat,      l: 'Fat',     cls: 'kpi-purple',  sfx: 'g'  },
            { v: food_today.sodium,   l: 'Sodium',  cls: 'kpi-red',     sfx: 'mg' },
            { v: food_today.sugar,    l: 'Sugar',   cls: 'kpi-magenta', sfx: 'g'  },
          ].map(({ v, l, cls, sfx }) => (
            <div key={l} className={`kpi-card ${cls}`}>
              <div className="kpi-value">{Math.round(Number(v))}{sfx}</div>
              <div className="kpi-label">{l}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <StatBar label="Calories" value={Number(food_today.calories)} max={goals.calories}  color="var(--accent-orange)"  />
          <StatBar label="Protein"  value={Number(food_today.protein)}  max={goals.protein_g} color="var(--accent-cyan)"    unit="g"  />
          <StatBar label="Carbs"    value={Number(food_today.carbs)}    max={goals.carbs_g}   color="var(--accent-gold)"    unit="g"  />
          <StatBar label="Fat"      value={Number(food_today.fat)}      max={goals.fat_g}     color="var(--accent-purple)"  unit="g"  />
          <StatBar label="Sodium"   value={Number(food_today.sodium)}   max={goals.sodium_mg} color="var(--accent-red)"     unit="mg" />
          <StatBar label="Sugar"    value={Number(food_today.sugar)}    max={goals.sugar_g}   color="var(--accent-magenta)" unit="g"  />
        </div>
      </div>

      {/* ── Budget ──────────────────────────────────────────────────────── */}
      <div className="section-label">💰 Monthly Budget</div>
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 24 }}>
        <div className="kpi-card kpi-gold">
          <div className="kpi-value">${Number(budget.monthly_total).toFixed(2)}</div>
          <div className="kpi-label">This Month</div>
        </div>
        <div className="kpi-card kpi-cyan">
          <div className="kpi-value">${Number(budget.recurring).toFixed(2)}</div>
          <div className="kpi-label">Recurring</div>
        </div>
        <div className="kpi-card kpi-green">
          <div className="kpi-value">${Number(budget.one_time).toFixed(2)}</div>
          <div className="kpi-label">One-Time</div>
        </div>
      </div>

      {/* ── Activity feed ───────────────────────────────────────────────── */}
      {activity.length > 0 && (
        <>
          <div className="section-label">🕐 Recent Activity</div>
          <div className="card activity-feed">
            {activity.map((item, i) => (
              <div key={`${item.kind}-${item.id}-${i}`} className="activity-item">
                <span className="activity-icon">
                  {item.kind === 'quest_complete' ? (QUEST_ICONS[item.type] || '✅') : '🎁'}
                </span>
                <div className="activity-body">
                  <div className="activity-name">{item.name}</div>
                  <div className="activity-meta">
                    {item.kind === 'quest_complete'
                      ? <span style={{ color: 'var(--accent-green)' }}>+{item.points} pts earned</span>
                      : <span style={{ color: 'var(--accent-red)'   }}>−{item.points} pts spent</span>
                    }
                    <span style={{ color: 'var(--text-muted)' }}>
                      {' · '}{new Date(item.time).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}